import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useProductSearch } from "../../../hooks/useProductSearch";
import ProductCard from "../ProductCard";
import { Skeleton } from "../../ui/skeleton";
import { Sparkles } from "lucide-react";

interface FiltersType {
  search: string;
  categories: string[];
  tags: string[];
  difficulty: string[];
  estimatedTimeMin: number | undefined;
  estimatedTimeMax: number | undefined;
  priceMin: number | undefined;
  priceMax: number | undefined;
  vendorId: string | undefined;
}

// Mirrors marketplace/pwfs/index.tsx's browse-grid shape exactly, backed by
// useProductSearch({type: "program"}) instead of "pwf". A Program purchase
// always results in a direct assignment (+ mentor relationship), never a
// trial, so no trial logic here either.
function ProgramProducts({ vendorId, filters, ragResults, ragLoading }: { vendorId?: string; filters: FiltersType; ragResults?: any[] | null; ragLoading?: boolean }) {
  const { t } = useTranslation();

  const { data: programProducts = [], isLoading: loading } = useProductSearch({
    type: "program",
    filters,
    vendorId,
  });

  const hasActiveFilters = useMemo(() =>
    filters.search || filters.categories.length > 0 || filters.tags.length > 0 || filters.difficulty.length > 0 || filters.vendorId || filters.estimatedTimeMin !== undefined || filters.estimatedTimeMax !== undefined || filters.priceMin !== undefined || filters.priceMax !== undefined,
    [filters]
  );

  const isRagMode = ragResults !== null && ragResults !== undefined;
  const displayProducts = isRagMode ? ragResults : programProducts;
  const displayLoading = isRagMode ? !!ragLoading : loading;

  return (
    <div>
      {isRagMode && !displayLoading && displayProducts.length > 0 && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-primary/5 border border-primary/10 rounded-lg">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm text-primary/80 font-medium">
            {t("ai_search_results") || "Showing AI-powered results"}
          </span>
          <span className="text-xs text-gray-500 ms-auto">
            {displayProducts.length} {t("results") || "results"}
          </span>
        </div>
      )}

      {displayLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array(8).fill(0).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white ring-1 ring-gray-200/70 shadow-sm overflow-hidden flex flex-col animate-pulse">
              <Skeleton className="h-40 w-full rounded-none bg-gradient-to-br from-gray-100 to-gray-200" />
              <div className="p-4 flex-1">
                <Skeleton className="h-4 w-3/4 rounded-lg bg-gray-100 mb-2" />
                <Skeleton className="h-3 w-2/5 rounded-lg bg-gray-100 mb-3" />
                <div className="flex gap-1.5 mb-3">
                  <Skeleton className="h-4 w-14 rounded-full bg-gray-100" />
                  <Skeleton className="h-4 w-18 rounded-full bg-gray-100" />
                </div>
                <Skeleton className="h-3 w-full rounded bg-gray-100 mb-1.5" />
                <Skeleton className="h-3 w-4/5 rounded bg-gray-100" />
              </div>
              <div className="px-4 py-3 bg-gray-50/80 flex gap-2">
                <Skeleton className="h-8 flex-1 rounded-lg bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!displayLoading && displayProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 mb-5 rounded-2xl bg-gradient-to-br from-primary/10 to-purple-100 flex items-center justify-center shadow-sm">
            <Sparkles className="w-7 h-7 text-primary/50" />
          </div>
          <p className="text-gray-700 font-semibold mb-1.5 text-base">
            {isRagMode
              ? t("no_ai_results") || "No matching results found"
              : hasActiveFilters
              ? t("no_products_found") || "No products match your filters"
              : t("marketplace_unavailable") || "No products available yet"}
          </p>
          <p className="text-gray-400 text-sm max-w-xs leading-relaxed">
            {isRagMode
              ? t("try_different_query") || "Try a different search query or browse all products"
              : hasActiveFilters
              ? t("try_different_filters") || "Try adjusting or clearing your filters"
              : t("check_back_later") || "Check back soon — new products are added regularly"}
          </p>
        </div>
      )}

      {!displayLoading && displayProducts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayProducts.map((product) => (
            <ProductCard
              key={product._id}
              product={product}
              buttonLayout="horizontal"
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ProgramProducts;
